import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRecoilValue,
  useSetRecoilState,
  useRecoilState,
  useResetRecoilState,
  useRecoilValueLoadable
} from 'recoil';
import LogRocket from 'logrocket';
import _ from 'lodash';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { useMount, useLocalStorage, useLocation } from 'react-use';
import { AnimatePresence } from 'framer-motion';
import {
  locationAtom,
  orderAtom,
  serviceAtom,
  storeAtom,
  themeAtom,
  stateAtom,
  tokenAtom,
  userAtom,
  loyaltyPointsAtom
} from '../atoms/Atoms';
import routes from '../routes';
import Notifications from './shared-components/Notifications';
import Sidebar from './shared-components/sidebar/Sidebar';
import Alert from './shared-components/Alert';
import ProtectedRoute from '../routes/ProtectedRoute';
import Chat from './shared-components/chat/Chat';
import DetailsModal from './pages/profile/shared-components/orders/shared-components/details-modal/DetailsModal';
import Header from './shared-components/header/Header';
import Subheader from './shared-components/header/shared-components/Subheader';
import Locations from './shared-components/locations/Locations';
import Navigator from './shared-components/navigator/Navigator';
import Modal from './shared-components/modal/Modal';
import api from '../api/api';
import {
  businessesSelector
} from '../atoms/Selectors';
import envConfig from '../envConfig';

function App() {
  if (!String.prototype.format) {
    String.prototype.format = function () {
      const args = arguments;
      return this.replace(/{(\d+)}/g, function (match, number) { 
        return typeof args[number] !== 'undefined'
          ? args[number]
          : match;
      });
    };
  }

  const { i18n } = useTranslation();
  const location = useLocation();
  const refreshStore = useResetRecoilState(businessesSelector);
  // Global States (Atoms)
  const theme = useRecoilValue(themeAtom);
  const setService = useSetRecoilState(serviceAtom);
  const setStore = useSetRecoilState(storeAtom);
  const setToken = useSetRecoilState(tokenAtom);
  const setState = useSetRecoilState(stateAtom);
  const setUser = useSetRecoilState(userAtom);
  const setLoyaltyPoints = useSetRecoilState(loyaltyPointsAtom);
  const [locations, setLocations] = useRecoilState(locationAtom);
  const [order, setOrder] = useRecoilState(orderAtom);
  // Local storage on mount
  const [service] = useLocalStorage('lhf-service');
  const [store] = useLocalStorage('lhf-store');
  const [token] = useLocalStorage('lhf-atoken');
  const [language] = useLocalStorage('lhf-lang');
  const [user] = useLocalStorage('lhf-user');
  const [state] = useLocalStorage('lhf-state');
  const [localLocation] = useLocalStorage('lhf-location');
  const [value, setValue, remove] = useLocalStorage('lhf-order');
  const [config, setConfig] = useState({});

  function refreshConfig() {
    api.get('configuration')
    .then((response) => {
      setConfig(response.data);
    })
    .catch((err) => {
      console.error(err.message);
    });
  }

  // Send local storage to global state on mount
  useMount(() => {
    setInterval(() => {
      refreshConfig();
    }, 300000);
    refreshStore();
    refreshConfig();
    if (envConfig.UseLogRocket === 'true') {
      LogRocket.init('uy6uyx/la-hacienda-foods');
    }
    if (language) i18n.changeLanguage(language);
    if (store) {
      setStore(store);
    }

    if (token) {
      setToken(token);
    }

    if (state) {
      setState(state);
    }

    if (user) {
      setUser(user);
    }

    if (value) {
      setOrder(value);
    }

    if (service) {
      setService(service);
    }

    if (localLocation) {
      setLocations({ ...locations, location: localLocation, preventRedirect: false });
    }
  });

  // This is the function that check everytime the order mutate
  // and persists the order on the local storage
  useEffect(() => {
    if (order.length > 0) {
      setValue(order);
    } else {
      remove();
    }
  }, [order]);

  useEffect(() => {
    if (!locations.modal) {
      setLocations({ ...locations, preventRedirect: false });
    }
  }, [location]);

  // useEffect(() => {
  //   api.get('loyalty/points')
  //     .then((response) => {
  //       setLoyaltyPoints(response.data.data);
  //     })
  // }, []);

  return (
    <div
      className={`${theme} flex flex-col h-screen overflow-y-auto md:overflow-hidden bg-gray-100 text-gray-800 font-body antialiased`}
    >
      <Router>
        <Notifications />
        <Locations />
        <Sidebar />
        <Alert />
        <Chat />
        <DetailsModal />
        <Header />
        <Navigator />
        <Modal />
        <AnimatePresence exitBeforeEnter initial={false}>
          <Switch location={location} key={location.pathname}>
            {_.map(routes, (route) => {
              if (route.protected) {
                return (
                  <ProtectedRoute
                    key={route.id}
                    path={route.path}
                    exact={route.exact}
                    component={route.component}
                  />
                );
              }

              return (
                <Route
                  key={route.id}
                  path={route.path}
                  exact={route.exact}
                  component={route.component}
                />
              );
            })}
          </Switch>
        </AnimatePresence>
      </Router>
      {config?.MaintenanceMode && (
        <div
          className="fixed bg-gray-800 inset-0 bg-opacity-75 z-10 flex items-end md:items-center justify-center px-0 md:px-4"
        >
          <div className="text-gray-800 justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="mx-auto max-w-md px-4">
              <div className="text-center rounded-xl shadow-lg relative flex flex-col px-4 py-2 w-full bg-white">
                {config?.MaintenanceModeTextEng?.length > 0 && (
                  <>{config?.MaintenanceModeTextEng}</>
                )}
                {config?.MaintenanceModeTextEng?.length > 0 &&
                config?.MaintenanceModeTextSpa?.length > 0 && (
                  <hr className="my-2" />
                )}
                {config?.MaintenanceModeTextSpa?.length > 0 && (
                  <>{config?.MaintenanceModeTextSpa}</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
